"use client";

import { useState, useEffect } from "react";

const sections = [
  { id: "data-collection", label: "01. Data Collection" },
  { id: "third-party", label: "02. Third-Party Services" },
  { id: "confidentiality", label: "03. Client Confidentiality" },
  { id: "security", label: "04. Security Protocols" },
  { id: "rights", label: "05. Your Rights" },
];

const STICKY_OFFSET = 112;

const PrivacyPage = () => {
  const [activeSection, setActiveSection] = useState(sections[0].id);

  useEffect(() => {
    function updateActive() {
      const passed = sections.filter(({ id }) => {
        const el = document.getElementById(`sentinel-${id}`);
        return el && el.getBoundingClientRect().top <= STICKY_OFFSET;
      });
      setActiveSection(
        passed.length > 0 ? passed[passed.length - 1].id : sections[0].id,
      );
    }
    window.addEventListener("scroll", updateActive, { passive: true });
    updateActive();
    return () => window.removeEventListener("scroll", updateActive);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(`sentinel-${id}`);
    if (!el) return;
    const y =
      el.getBoundingClientRect().top + window.scrollY - STICKY_OFFSET + 1;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  return (
    <div className="font-body-md text-on-surface">
      <main className="w-full bg-white">
        <div className="max-w-[1440px] mx-auto px-margin-mobile md:px-margin-desktop">
          {/* Hero */}
          <section className="pt-20 pb-20 border-b border-outline-variant">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
              <div className="max-w-3xl">
                <span className="font-label-sm uppercase tracking-widest text-on-surface-variant mb-4 block">
                  Legal Framework
                </span>
                <h1 className="font-display text-display text-primary mb-6">
                  Privacy Policy
                </h1>
                <p className="font-body-lg text-on-surface-variant leading-relaxed">
                  Shecraa Studio (shecraa.store), based in Jakarta, Indonesia,
                  is committed to data integrity and client confidentiality.
                  This policy covers Website Development, iOS App Development,
                  and Python Automation Solutions. Contact gustavohoze@gmail.com
                  for inquiries.
                </p>
              </div>
              <div className="flex flex-col items-start md:items-end shrink-0">
                <span className="font-label-sm text-on-surface-variant mb-1">
                  Last Updated
                </span>
                <span className="font-body-md font-bold text-primary">
                  May 06, 2026
                </span>
                <a
                  href="/privacy.pdf"
                  download
                  className="mt-4 flex items-center gap-2 bg-primary-container text-on-primary px-4 py-2 rounded text-sm font-medium active:scale-95 transition-transform border border-outline-variant"
                >
                  <span className="material-symbols-outlined text-[18px]">
                    download
                  </span>
                  Download PDF
                </a>
              </div>
            </div>
          </section>

          {/* Body — NO gap-y, NO space-y on article, so both columns start flush */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-x-8 pt-20 pb-section-gap">
            {/* Sidebar */}
            <aside className="md:col-span-3 hidden md:block sticky top-28 self-start">
              <div className="border border-outline-variant rounded-lg p-6 bg-surface-container">
                <nav className="flex flex-col gap-1">
                  {sections.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => scrollTo(s.id)}
                      className="w-full text-left px-3 py-2 rounded text-sm transition-colors"
                      style={
                        activeSection === s.id
                          ? {
                              background: "#ffffff",
                              border: "1.5px solid #111111",
                              color: "#111111",
                              fontWeight: 500,
                            }
                          : {
                              background: "none",
                              border: "1.5px solid transparent",
                              color: "inherit",
                              fontWeight: 400,
                            }
                      }
                    >
                      {s.label}
                    </button>
                  ))}
                </nav>
                <div className="pt-6 mt-6 border-t border-outline-variant">
                  <p className="text-xs text-on-surface-variant leading-tight">
                    For legal inquiries regarding these terms, please contact
                    our compliance desk directly.
                  </p>
                </div>
              </div>
            </aside>

            {/* Article — no space-y, sections use mb-24 so first child has no top offset */}
            <article className="md:col-span-9 md:pl-gutter">
              {/* Section 01 */}
              <div className="mb-24">
                <span
                  id="sentinel-data-collection"
                  className="block h-0 overflow-hidden"
                />
                <div className="flex items-center gap-4 mb-4">
                  <span className="font-label-sm bg-surface-container px-2 py-1 text-on-surface-variant">
                    01
                  </span>
                  <h2 className="font-h2 text-h2 text-primary">
                    Data Collection
                  </h2>
                </div>
                <div className="h-px bg-outline-variant mb-6" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                  <div className="space-y-4">
                    <h3 className="font-h3 text-h3">
                      Personal &amp; Project Data
                    </h3>
                    <p className="font-body-md text-on-surface-variant">
                      We collect full names, email addresses, phone numbers, and
                      project/business details provided during consultations —
                      used for service delivery, ongoing support, and fraud
                      prevention.
                    </p>
                  </div>
                  <div className="space-y-4">
                    <h3 className="font-h3 text-h3">
                      Technical &amp; Financial
                    </h3>
                    <p className="font-body-md text-on-surface-variant">
                      We collect payment and invoicing information for
                      transactions. Device, browser, IP, and cookie analytics
                      are gathered to optimize performance and security.
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 02 */}
              <div className="mb-24">
                <span
                  id="sentinel-third-party"
                  className="block h-0 overflow-hidden"
                />
                <div className="flex items-center gap-4 mb-4">
                  <span className="font-label-sm bg-surface-container px-2 py-1 text-on-surface-variant">
                    02
                  </span>
                  <h2 className="font-h2 text-h2 text-primary">
                    Third-Party Services
                  </h2>
                </div>
                <div className="h-px bg-outline-variant mb-6" />
                <p className="font-body-md text-on-surface-variant max-w-2xl mb-4">
                  Our infrastructure leverages industry-leading vendors vetted
                  for compliance with global privacy standards including GDPR
                  and CCPA.
                </p>
                <p className="font-body-md text-on-surface-variant max-w-2xl mb-8">
                  By using our services, you acknowledge your data may be
                  transferred to providers outside Indonesia, subject to strict
                  security protocols.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { icon: "cloud", label: "Cloud Hosting" },
                    { icon: "analytics", label: "Analytics" },
                    { icon: "payments", label: "Payment Proc" },
                    { icon: "mail", label: "SMTP Delivery" },
                  ].map(({ icon, label }) => (
                    <div
                      key={label}
                      className="border border-outline-variant p-6 flex flex-col items-center gap-2 text-center"
                    >
                      <span
                        className="material-symbols-outlined text-primary"
                        style={{ fontVariationSettings: "'opsz' 48" }}
                      >
                        {icon}
                      </span>
                      <span className="font-label-sm text-on-surface-variant">
                        {label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Section 03 */}
              <div className="mb-24">
                <span
                  id="sentinel-confidentiality"
                  className="block h-0 overflow-hidden"
                />
                <div className="flex items-center gap-4 mb-4">
                  <span className="font-label-sm bg-surface-container px-2 py-1 text-on-surface-variant">
                    03
                  </span>
                  <h2 className="font-h2 text-h2 text-primary">
                    Client Confidentiality
                  </h2>
                </div>
                <div className="h-px bg-outline-variant mb-6" />
                <div className="bg-surface-container p-12 border border-outline-variant space-y-6">
                  <p className="font-body-lg text-primary italic font-medium">
                    &ldquo;We treat your source code and business logic with the same
                    reverence as a master architect treats a structural
                    blueprint.&rdquo;
                  </p>
                  <p className="font-body-md text-on-surface-variant">
                    We do not sell your personal data. Sharing only occurs with
                    trusted service providers essential for operations or when
                    mandated by legal and security requirements.
                  </p>
                </div>
              </div>

              {/* Section 04 */}
              <div className="mb-24">
                <span
                  id="sentinel-security"
                  className="block h-0 overflow-hidden"
                />
                <div className="flex items-center gap-4 mb-4">
                  <span className="font-label-sm bg-surface-container px-2 py-1 text-on-surface-variant">
                    04
                  </span>
                  <h2 className="font-h2 text-h2 text-primary">
                    Security Protocols
                  </h2>
                </div>
                <div className="h-px bg-outline-variant mb-6" />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {[
                    {
                      title: "Encryption",
                      body: "All data in transit is shielded via TLS 1.3; data at rest uses AES-256 standards.",
                    },
                    {
                      title: "Access Control",
                      body: "Strict least-privilege models ensure only necessary personnel interact with sensitive data.",
                    },
                    {
                      title: "Audit Trails",
                      body: "We maintain immutable logs of system interactions to detect and prevent unauthorized access.",
                    },
                  ].map(({ title, body }) => (
                    <div key={title} className="space-y-3">
                      <h3 className="font-body-md font-bold text-primary">
                        {title}
                      </h3>
                      <p className="text-sm text-on-surface-variant">{body}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Section 05 */}
              <div className="mb-0">
                <span
                  id="sentinel-rights"
                  className="block h-0 overflow-hidden"
                />
                <div className="flex items-center gap-4 mb-4">
                  <span className="font-label-sm bg-surface-container px-2 py-1 text-on-surface-variant">
                    05
                  </span>
                  <h2 className="font-h2 text-h2 text-primary">Your Rights</h2>
                </div>
                <div className="h-px bg-outline-variant mb-6" />
                <ul className="space-y-4 font-body-md text-on-surface-variant">
                  {[
                    {
                      label: "Access",
                      body: "The right to request a portable copy of your personal data stored within our systems.",
                    },
                    {
                      label: "Correction",
                      body: "The right to challenge and rectify the accuracy of records held within our environments.",
                    },
                    {
                      label: "Deletion",
                      body: "The right to request the absolute removal of all personal metadata from our servers.",
                    },
                  ].map(({ label, body }) => (
                    <li key={label} className="flex gap-4">
                      <span className="material-symbols-outlined text-primary text-sm mt-0.5">
                        check_circle
                      </span>
                      <span>
                        <b>{label}:</b> {body}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPage;
